import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { formatNameForDisplay } from '../../utils/nameUtils';
import { getPlayerNameColor, buildPlayerEloTooltipText, formatSessionGamesCountLabel } from '../../utils/playerUtils';
import { getGenderAvatarBg } from '../../utils/genderColors';
import EloChange from '../common/EloChange';
import { getPlayerAvatarPublicUrl } from '../../services/avatarService';

const STATS_TOOLTIP_WIDTH_DEFAULT = 280;
const STATS_TOOLTIP_EST_HEIGHT_DEFAULT = 96;
const STATS_TOOLTIP_GAP_DEFAULT = 6;

/** Caret points toward the name anchor; `placement` is where the box sits relative to the anchor. */
export function StatsTooltipCaret({ placement }) {
  if (placement === 'above') {
    return (
      <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px" aria-hidden>
        <div className="h-0 w-0 border-l-[6px] border-r-[6px] border-t-[6px] border-transparent border-t-border-medium dark:border-t-surface-elevated" />
      </div>
    );
  }
  return (
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-px" aria-hidden>
      <div className="h-0 w-0 border-l-[6px] border-r-[6px] border-b-[6px] border-transparent border-b-border-medium dark:border-b-surface-elevated" />
    </div>
  );
}

/**
 * Stats tooltip: prefers just above the pointer (or above the anchor when using keyboard).
 * Horizontal position follows the pointer when available; otherwise uses anchor center.
 * @param {{ width?: number, estHeight?: number, gap?: number }} [options]
 */
export function useStatsTooltip(options = {}) {
  const tooltipWidthEst = options.width ?? STATS_TOOLTIP_WIDTH_DEFAULT;
  const estHeight = options.estHeight ?? STATS_TOOLTIP_EST_HEIGHT_DEFAULT;
  const gap = options.gap ?? STATS_TOOLTIP_GAP_DEFAULT;

  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = useRef(null);
  /** Portal root — used to measure real width/height so fixed position matches the cursor. */
  const floatingRef = useRef(null);
  const pointerRef = useRef(null);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const [placement, setPlacement] = useState('above');

  const reposition = useCallback(() => {
    const el = tooltipRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const pointer = pointerRef.current;
    const usePointer = pointer && Number.isFinite(pointer.x) && Number.isFinite(pointer.y);

    const fr = floatingRef.current?.getBoundingClientRect();
    const w = fr && fr.width > 2 ? fr.width : tooltipWidthEst;
    const h = fr && fr.height > 2 ? fr.height : estHeight;

    const anchorCenterX = rect.left + rect.width / 2;

    let left;
    let top;
    let nextPlacement;

    if (usePointer) {
      top = pointer.y - h - gap;
      nextPlacement = 'above';
      if (top < 8) {
        top = pointer.y + gap;
        nextPlacement = 'below';
      }
      left = pointer.x - w / 2;
    } else {
      top = rect.top - h - gap;
      nextPlacement = 'above';
      if (top < 8) {
        top = rect.bottom + gap;
        nextPlacement = 'below';
      }
      left = anchorCenterX - w / 2;
    }

    if (left + w > vw - 8) left = vw - w - 8;
    if (left < 8) left = 8;

    if (top + h > vh - 8) top = vh - h - 8;
    if (top < 8) top = 8;

    setPlacement(nextPlacement);
    setTooltipStyle({
      position: 'fixed',
      top: `${Math.round(top)}px`,
      left: `${Math.round(left)}px`,
      zIndex: 99999,
    });
  }, [tooltipWidthEst, estHeight, gap]);

  useLayoutEffect(() => {
    if (!showTooltip) return;
    reposition();
    const id = window.requestAnimationFrame(() => reposition());
    return () => window.cancelAnimationFrame(id);
  }, [showTooltip, reposition]);

  useEffect(() => {
    if (!showTooltip) return;
    const onMove = (e) => {
      pointerRef.current = { x: e.clientX, y: e.clientY };
      reposition();
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, [showTooltip, reposition]);

  useEffect(() => {
    if (!showTooltip) return;
    const handleClickOutside = (e) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target)) {
        setShowTooltip(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTooltip]);

  const onAnchorMouseEnter = useCallback((e) => {
    if (e && typeof e.clientX === 'number' && typeof e.clientY === 'number') {
      pointerRef.current = { x: e.clientX, y: e.clientY };
    }
  }, []);

  /** Call when opening the tooltip from keyboard so position uses anchor, not stale pointer. */
  const onAnchorKeyboardOpen = useCallback(() => {
    pointerRef.current = null;
  }, []);

  return {
    showTooltip,
    setShowTooltip,
    tooltipRef,
    floatingRef,
    tooltipStyle,
    placement,
    onAnchorMouseEnter,
    onAnchorKeyboardOpen,
  };
}

/**
 * Performance badge from win streak (🔥 3+, 💎 5+).
 * @returns {{ icon: string, tooltip: string } | null}
 */
export function getPerformanceBadge(playerStreaks, playerId) {
  const streak = playerStreaks?.[playerId];
  if (!streak) return null;
  const consecutive = streak.currentWinStreak ?? 0;
  if (consecutive >= 5) return { icon: '💎', tooltip: 'Elite Form: 5+ consecutive wins' };
  if (consecutive >= 3) return { icon: '🔥', tooltip: 'Hot Streak: 3+ consecutive wins' };
  return null;
}

const VARIANT_DEFAULTS = {
  court: {
    showRemove: true,
    showGames: true,
    showPerformanceBadge: true,
    showEloChange: true,
    layout: 'vertical',
    nameMaxLength: 22,
    className: 'group flex flex-col items-center justify-center w-full px-3 py-3 rounded-lg shadow-sm text-lg font-semibold bg-surface dark:bg-slate-700 border border-border hover:bg-surface-subtle dark:hover:bg-slate-600 relative select-none min-h-[76px]'
  },
  nextCourt: {
    showRemove: true,
    showGames: true,
    showPerformanceBadge: false,
    showEloChange: false,
    layout: 'vertical',
    nameMaxLength: 17,
    className: 'group flex flex-col items-center justify-center w-[148px] h-[76px] px-4 py-3 m-1 rounded-xl shadow-md text-lg font-semibold bg-surface dark:bg-slate-700 border border-border-medium hover:bg-surface-subtle dark:hover:bg-slate-600 relative select-none'
  },
  upcoming: {
    showRemove: true,
    showGames: true,
    showPerformanceBadge: false,
    showEloChange: false,
    layout: 'vertical',
    nameMaxLength: 17,
    className: 'group flex flex-col items-start px-4 py-2.5 m-1 rounded-xl shadow-sm text-lg font-semibold bg-info-light/80 border-2 border-info hover:bg-info-light hover:border-info relative select-none min-w-0 max-w-xs min-h-[84px] pr-2 transition-all duration-150'
  },
  compact: {
    showRemove: false,
    showGames: true,
    showPerformanceBadge: false,
    showEloChange: false,
    layout: 'horizontal',
    nameMaxLength: 22,
    className: ''
  },
  waitingCourt: {
    showRemove: true,
    showGames: true,
    showPerformanceBadge: false,
    showEloChange: false,
    layout: 'vertical',
    nameMaxLength: 22,
    className: 'inline-flex items-center px-3 py-1.5 m-1 rounded-full shadow text-lg font-semibold bg-surface dark:bg-slate-700 border border-border-medium relative'
  }
};

/**
 * Shared session player pill. Use variant to match previous per-context behaviour.
 *
 * @param {Object} player - Player object (id, name, gender, gamesPlayedThisSession, effectiveGamesPlayed, mixed_elo_rating, levels_elo_rating)
 * @param {Object} [teammate] - For getDisplayElo
 * @param {'court'|'nextCourt'|'upcoming'|'compact'|'waitingCourt'} [variant='court']
 * @param {Function} [onRemove] - (playerId, team, courtNumber?) => void
 * @param {number} [team] - Team number (1 or 2)
 * @param {number} [courtNumber]
 * @param {number|object} [eloDelta] - ELO change (number or { total })
 * @param {Object} [playerStreaks] - { [playerId]: { currentWinStreak } }
 * @param {string} [borderColor] - For compact (e.g. 'border-team1')
 * @param {string} [teamColor] - For compact (e.g. 'team1')
 * @param {string} [removeTitle] - Title for remove button
 */
const SessionPlayerPill = ({
  player,
  teammate = null,
  variant = 'court',
  onRemove,
  team,
  courtNumber,
  eloDelta,
  playerStreaks = {},
  borderColor = '',
  teamColor = '',
  removeTitle = 'Remove',
  useSinglesElo = false,
  waitMetricSlice = null,
  showEloTooltip = true,
}) => {
  const {
    showTooltip,
    setShowTooltip,
    tooltipRef,
    floatingRef,
    tooltipStyle,
    placement,
    onAnchorMouseEnter,
    onAnchorKeyboardOpen,
  } = useStatsTooltip({
    width: 300,
    estHeight: 96,
    gap: 6,
  });

  if (!player) return null;

  const opts = { ...VARIANT_DEFAULTS[variant] };
  // Gender accent for court/nextCourt: surface background + colored left border so pills fit dark mode
  if (variant === 'court' || variant === 'nextCourt') {
    const borderAccent = player.gender?.toLowerCase() === 'female' ? 'border-l-4 border-l-female' : 'border-l-4 border-l-info';
    opts.className = [opts.className, borderAccent].filter(Boolean).join(' ');
  }
  const displayEloChange = typeof eloDelta === 'object' && eloDelta !== null ? eloDelta.total : eloDelta;
  const statsTooltipStr = buildPlayerEloTooltipText(player, {
    useSinglesElo,
    eloChangeTotal: opts.showEloChange ? displayEloChange : null,
    sessionWaitMetrics: waitMetricSlice,
  });
  const gamesLabel = formatSessionGamesCountLabel(player);
  const performanceBadge = opts.showPerformanceBadge ? getPerformanceBadge(playerStreaks, player.id) : null;
  const nameFormat = formatNameForDisplay(player.name, {
    maxLength: opts.nameMaxLength,
    useInitials: true,
    initialsThreshold: 25
  });
  const displayName = nameFormat.display || player.name;
  const canHover = typeof window !== 'undefined' && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  const handleRemove = (e) => {
    e.stopPropagation();
    if (onRemove) onRemove(player.id, team, courtNumber);
  };

  const ratingsNameProps = showEloTooltip
    ? {
        onMouseEnter: (e) => {
          onAnchorMouseEnter(e);
          if (canHover) setShowTooltip(true);
        },
        onMouseLeave: () => canHover && setShowTooltip(false),
        onClick: (e) => {
          e.stopPropagation();
          if (!canHover) setShowTooltip((p) => !p);
        },
        onFocus: () => {
          onAnchorKeyboardOpen();
          setShowTooltip(true);
        },
        onBlur: () => setShowTooltip(false),
        tabIndex: 0,
        role: 'button',
        'aria-label': `View ratings for ${player.name || displayName}`,
      }
    : {};

  const statsTooltipPortal =
    showEloTooltip &&
    showTooltip &&
    createPortal(
      <div
        ref={floatingRef}
        className="relative max-w-xs px-3 py-2 text-sm rounded-lg pointer-events-none border border-border-medium bg-surface-subtle text-text-primary shadow-lg dark:bg-surface-elevated dark:text-text-primary dark:shadow-xl"
        style={tooltipStyle}
      >
        <div className="font-medium whitespace-pre-line">{statsTooltipStr}</div>
        <StatsTooltipCaret placement={placement} />
      </div>,
      document.body
    );

  if (variant === 'compact') {
    return (
      <>
        <div className={`bg-surface border ${borderColor} rounded-lg p-2.5 flex items-center justify-between gap-2 min-h-[3.25rem] ${opts.className}`}>
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden ${getGenderAvatarBg(player.gender)}`}>
              {player.avatar_url ? (
                <img
                  src={getPlayerAvatarPublicUrl(player.avatar_url)}
                  alt={player.fullName || player.name || ''}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-sm font-semibold text-white">
                  {(player.fullName || player.name || '').charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span
                  ref={tooltipRef}
                  className={`text-lg font-semibold truncate min-w-0 ${showEloTooltip ? 'cursor-help focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded' : ''} ${getPlayerNameColor(player.gender)}`}
                  {...ratingsNameProps}
                >
                  {player.name || displayName}
                </span>
                {opts.showGames && (
                  <span className="text-sm text-text-muted tabular-nums shrink-0" title="Games this session">
                    {gamesLabel}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
        {statsTooltipPortal}
      </>
    );
  }

  if (variant === 'waitingCourt') {
    return (
      <>
        <li className={opts.className} style={{ position: 'relative', boxSizing: 'border-box' }}>
          {opts.showRemove && onRemove && (
            <button
              onClick={handleRemove}
              className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full border border-border bg-danger-light text-xs text-danger-dark shadow hover:bg-danger"
              title={removeTitle}
            >
              -
            </button>
          )}
          <div className="flex min-w-0 flex-col gap-1">
            <span
              ref={tooltipRef}
              className={`text-lg font-semibold truncate ${showEloTooltip ? 'cursor-help focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded' : ''} ${getPlayerNameColor(player.gender)}`}
              {...ratingsNameProps}
            >
              {player.name}
            </span>
            {opts.showGames && (
              <span className="text-sm tabular-nums text-text-muted" title="Games this session">
                {gamesLabel}
              </span>
            )}
          </div>
        </li>
        {statsTooltipPortal}
      </>
    );
  }

  const removeBtnClass =
    variant === 'upcoming'
      ? 'absolute -top-2 -right-2 z-20 bg-danger hover:bg-danger-dark text-text-inverse rounded-full w-6 h-6 flex items-center justify-center shadow-md text-xs border-2 border-info-light'
      : 'absolute -top-1.5 -right-1.5 z-20 bg-danger-light hover:bg-danger text-danger-dark rounded-full w-5 h-5 flex items-center justify-center shadow-sm text-xs border border-border';

  return (
    <>
      <li className={opts.className} style={{ position: 'relative', boxSizing: 'border-box' }}>
        {opts.showRemove && onRemove && (
          <button onClick={handleRemove} className={removeBtnClass} title={removeTitle}>
            -
          </button>
        )}
        <div className="flex flex-col w-full items-center justify-center gap-1.5">
          <div
            className={`flex w-full max-w-full items-center gap-1 min-h-[24px] ${variant === 'upcoming' ? 'justify-start' : 'justify-center'}`}
          >
            <span
              ref={tooltipRef}
              className={`font-semibold font-sans min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap ${showEloTooltip ? 'cursor-help focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded' : ''} ${getPlayerNameColor(player.gender)} ${
                variant === 'upcoming' ? 'text-left' : 'text-center'
              } ${
                nameFormat.isInitials ? 'text-sm font-bold max-w-[75%]' : 'text-lg sm:text-xl'
              }`}
              {...ratingsNameProps}
            >
              {displayName}
            </span>
            {performanceBadge && (
              <span
                className="shrink-0 text-base leading-none sm:text-lg"
                title={performanceBadge.tooltip}
                role="img"
                aria-label={performanceBadge.tooltip}
              >
                {performanceBadge.icon}
              </span>
            )}
          </div>
          {opts.showGames && (
            <span
              className={`w-full text-sm tabular-nums text-text-muted ${variant === 'upcoming' ? 'text-left' : 'text-center'}`}
              title="Games this session"
            >
              {gamesLabel}
            </span>
          )}
          {opts.showEloChange && displayEloChange !== undefined && displayEloChange !== 0 && (
            <div className="flex items-center justify-center gap-1.5">
              <span className="flex items-center">
                <EloChange change={displayEloChange} showSign size="sm" />
              </span>
            </div>
          )}
        </div>
      </li>
      {statsTooltipPortal}
    </>
  );
};

export default SessionPlayerPill;
